import { useState, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { useApp } from '../../context/AppContext'
import type { TextStats } from '../../lib/deduplication'
import { trackEvent } from '../../lib/analytics'

function StatBadge({ label, value, color }: { label: string; value: number; color: 'blue' | 'pink' }) {
  const textColor = color === 'blue' ? 'text-neonBlue' : 'text-neonPink'
  return (
    <div className="bg-slate-200/50 dark:bg-slate-800/60 p-3 rounded-twelve border border-slate-300 dark:border-slate-700 text-center">
      <p className="text-[10px] text-slate-600 dark:text-slate-200 uppercase font-black">{label}</p>
      <p className={`text-xl font-black ${textColor}`}>{value}</p>
    </div>
  )
}

function StatsRow({ stats, color }: { stats: TextStats; color: 'blue' | 'pink' }) {
  const { t } = useTranslation()
  return (
    <div className="grid grid-cols-4 gap-2">
      <StatBadge label={t('interface.stats.lines')} value={stats.lines} color={color} />
      <StatBadge label={t('interface.stats.words')} value={stats.words} color={color} />
      <StatBadge label={t('interface.stats.chars')} value={stats.chars} color={color} />
      <StatBadge label={t('interface.stats.noSpace')} value={stats.charsNoSpace} color={color} />
    </div>
  )
}

function IconBtn({ onClick, title, children, color = 'blue' }: {
  onClick: () => void
  title: string
  children: React.ReactNode
  color?: 'blue' | 'pink' | 'red'
}) {
  const colors = {
    blue: 'bg-neonBlue/10 dark:bg-neonBlue/20 hover:bg-neonBlue/20 dark:hover:bg-neonBlue/30 text-neonBlue border-neonBlue/50',
    pink: 'bg-neonPink/10 dark:bg-neonPink/20 hover:bg-neonPink/20 dark:hover:bg-neonPink/30 text-neonPink border-neonPink/50',
    red: 'bg-red-500/10 hover:bg-red-500/20 text-red-500 border-red-400/50',
  }
  return (
    <button
      onClick={onClick}
      title={title}
      className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-black border-2 transition-all active:scale-95 ${colors[color]}`}
    >
      {children}
    </button>
  )
}

export default function Interface() {
  const { t } = useTranslation()
  const { inputText, outputText, inputStats, outputStats, setInputText, clearInput } = useApp()
  const [copied, setCopied] = useState(false)
  const fileInputRef = useRef<HTMLInputElement>(null)

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(outputText)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
      trackEvent('copy_output', { output_lines: outputStats.lines })
    } catch {
      // fallback: no-op
    }
  }

  const handleUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return
    const reader = new FileReader()
    reader.onload = (ev) => {
      const text = ev.target?.result as string
      if (text) {
        setInputText(text)
        trackEvent('file_upload', { file_size_kb: Math.round(file.size / 1024) })
      }
    }
    reader.readAsText(file)
    e.target.value = ''
  }

  const handleDownload = () => {
    if (!outputText) return
    const blob = new Blob([outputText], { type: 'text/plain' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = 'cleaned.txt'
    a.click()
    URL.revokeObjectURL(url)
    trackEvent('file_download', { output_lines: outputStats.lines })
  }

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-12">
      {/* Input Panel */}
      <section className="flex flex-col gap-4">
        <div className="flex justify-between items-center px-2">
          <h2 className="text-2xl font-black text-neonBlue uppercase tracking-widest">
            {t('interface.inputLabel')}
          </h2>
          <div className="flex items-center gap-2">
            <input
              ref={fileInputRef}
              type="file"
              accept=".txt,.csv,text/plain,text/csv"
              className="hidden"
              onChange={handleUpload}
            />
            <IconBtn onClick={() => { fileInputRef.current?.click() }} title={t('interface.uploadButton')} color="blue">
              <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
              </svg>
              {t('interface.uploadButton')}
            </IconBtn>
            {inputText && (
              <IconBtn onClick={() => { clearInput(); trackEvent('clear_input') }} title={t('interface.clearButton')} color="red">
                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
                {t('interface.clearButton')}
              </IconBtn>
            )}
          </div>
        </div>
        <div className="relative clay-card rounded-twelve overflow-hidden">
          <textarea
            className="w-full h-[400px] bg-transparent border-none focus:ring-4 focus:ring-neonBlue/50 p-6 text-lg font-bold text-slate-900 dark:text-slate-100 placeholder:text-slate-400 dark:placeholder:text-slate-500 resize-none outline-none"
            placeholder={t('interface.inputPlaceholder')}
            value={inputText}
            onChange={(e) => setInputText(e.target.value)}
          />
        </div>
        <StatsRow stats={inputStats} color="blue" />
      </section>

      {/* Output Panel */}
      <section className="flex flex-col gap-4">
        <div className="flex justify-between items-center px-2">
          <h2 className="text-2xl font-black text-neonPink uppercase tracking-widest">
            {t('interface.outputLabel')}
          </h2>
          <div className="flex items-center gap-2">
            {outputText && (
              <IconBtn onClick={handleDownload} title={t('interface.downloadButton')} color="pink">
                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                </svg>
                {t('interface.downloadButton')}
              </IconBtn>
            )}
            <IconBtn onClick={handleCopy} title={t('interface.copyButton')} color="blue">
              <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  d="M8 5H6a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2v-1M8 5a2 2 0 002 2h2a2 2 0 002-2M8 5a2 2 0 012-2h2a2 2 0 012 2m0 0h2a2 2 0 012 2v3m2 4H10m0 0l3-3m-3 3l3 3"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                />
              </svg>
              {copied ? t('interface.copied') : t('interface.copyButton')}
            </IconBtn>
          </div>
        </div>
        <div className="relative clay-card rounded-twelve overflow-hidden border-neonPink/50">
          <textarea
            readOnly
            className="w-full h-[400px] bg-transparent border-none focus:ring-0 p-6 text-lg font-bold text-neonGreen placeholder:text-slate-400 dark:placeholder:text-slate-700 resize-none outline-none"
            placeholder={t('interface.outputPlaceholder')}
            value={outputText}
          />
        </div>
        <StatsRow stats={outputStats} color="pink" />
      </section>
    </div>
  )
}
